import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  FC,
} from "react";

type RetryAction = () => Promise<void>;

export type SetError = (
  message: string | null,
  actions?: { continue?: () => void; retry?: RetryAction }
) => void;

interface ApiErrorContextType {
  errorMessage: string | null;
  setError: SetError;
  actions: {
    retry?: RetryAction;
  };
}

const ApiErrorContext = createContext<ApiErrorContextType | undefined>(
  undefined
);

export const ApiErrorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actions, setActions] = useState<{
    retry?: RetryAction;
  }>({});

  const setError = (
    message: string | null,
    newActions?: {
      continue?: () => void;
      retry?: RetryAction;
    }
  ) => {
    setErrorMessage(message);
    setActions(newActions || {});
  };

  return (
    <ApiErrorContext.Provider
      value={{
        errorMessage,
        setError,
        actions,
      }}
    >
      {children}
    </ApiErrorContext.Provider>
  );
};

export const useApiError = () => {
  const context = useContext(ApiErrorContext);
  if (!context) {
    throw new Error("useApiError must be used within an ApiErrorProvider");
  }
  return context;
};
